import {
  BaseBoxShapeUtil,
  HTMLContainer,
  Rectangle2d,
  TLBaseShape,
  TLResizeInfo,
  BaseBoxShapeTool
} from "tldraw"
import React from "react"

// 30-60-90 triangle: if the short side (opposite 30°) = 1,
// then the long side (opposite 60°) = √3, hypotenuse = 2.
// Layout: right angle at bottom corner, short side along bottom, tall side vertical.
// Aspect ratio w:h = 1:√3

const ASPECT = Math.sqrt(3) // h / w

type SetSquare3060Shape = TLBaseShape<
  "set-square-30-60",
  {
    w: number
    h: number
    flipped: boolean
  }
>

export class SetSquare3060ShapeUtil extends BaseBoxShapeUtil<SetSquare3060Shape> {
  static override type = "set-square-30-60" as const

  getDefaultProps(): SetSquare3060Shape["props"] {
    return {
      w: 200,
      h: Math.round(200 * ASPECT),
      flipped: false,
    }
  }

  getGeometry(shape: SetSquare3060Shape) {
    return new Rectangle2d({
      width: shape.props.w,
      height: shape.props.h,
      isFilled: true,
    })
  }

  component(shape: SetSquare3060Shape) {
    const { w, h, flipped } = shape.props

    const padding = 10

    // Right angle at bottom-left (normal) or bottom-right (flipped)
    // Short side = bottom edge (opposite 30° at top)
    // Tall side = vertical edge (opposite 60° at bottom-far corner)
    const rightX = flipped ? w - padding : padding
    const rightY = h - padding
    const baseX = flipped ? padding : w - padding
    const baseY = h - padding
    const topX = flipped ? w - padding : padding
    const topY = padding

    const baseLen = Math.abs(baseX - rightX)
    const sideLen = rightY - topY

    const pxPerCm = 50
    const pxPerMm = pxPerCm / 10
    const dir = flipped ? -1 : 1

    const ticks: React.ReactNode[] = []

    // Bottom edge ticks
    const bottomTotalMm = Math.floor(baseLen / pxPerMm)
    for (let i = 0; i <= bottomTotalMm; i++) {
      const x = rightX + (flipped ? -1 : 1) * i * pxPerMm
      const isCm = i % 10 === 0
      const isHalf = i % 5 === 0

      let tickH = 8
      if (isHalf) tickH = 12
      if (isCm) tickH = 18

      ticks.push(
        <line
          key={`b-${i}`}
          x1={x} y1={rightY}
          x2={x} y2={rightY - tickH}
          stroke="black" strokeWidth={1}
        />
      )

      if (isCm) {
        ticks.push(
          <text
            key={`bt-${i}`}
            x={x} y={rightY - 22}
            fontSize={11}
            textAnchor="middle"
            dominantBaseline="middle"
          >
            {i / 10}
          </text>
        )
      }
    }

    // Vertical edge ticks
    const sideTotalMm = Math.floor(sideLen / pxPerMm)
    for (let i = 0; i <= sideTotalMm; i++) {
      const y = rightY - i * pxPerMm
      const isCm = i % 10 === 0
      const isHalf = i % 5 === 0

      let tickH = 8
      if (isHalf) tickH = 12
      if (isCm) tickH = 18

      ticks.push(
        <line
          key={`l-${i}`}
          x1={rightX} y1={y}
          x2={rightX + dir * tickH} y2={y}
          stroke="black" strokeWidth={1}
        />
      )

      if (isCm) {
        ticks.push(
          <text
            key={`lt-${i}`}
            x={rightX + dir * 22} y={y}
            fontSize={11}
            textAnchor="middle"
            dominantBaseline="middle"
          >
            {i / 10}
          </text>
        )
      }
    }

    // Right angle indicator
    const arcR = 20
    ticks.push(
      <path
        key="right-angle"
        d={`M ${rightX + dir * arcR} ${rightY} L ${rightX + dir * arcR} ${rightY - arcR} L ${rightX} ${rightY - arcR}`}
        fill="none"
        stroke="#333"
        strokeWidth={1}
      />
    )

    const degArcR = 30

    // 60° angle at bottom opposite corner (baseX, baseY)
    const angle60 = (60 * Math.PI) / 180
    const bArcStartX = baseX + (flipped ? 1 : -1) * degArcR
    const bArcEndX = baseX + (flipped ? 1 : -1) * degArcR * Math.cos(angle60)
    const bArcEndY = baseY - degArcR * Math.sin(angle60)
    const bSweep = flipped ? 0 : 1
    ticks.push(
      <path
        key="angle-base"
        d={`M ${bArcStartX} ${baseY} A ${degArcR} ${degArcR} 0 0 ${bSweep} ${bArcEndX} ${bArcEndY}`}
        fill="none"
        stroke="#333"
        strokeWidth={1}
      />
    )
    ticks.push(
      <text
        key="angle-base-text"
        x={baseX + (flipped ? 1 : -1) * 6}
        y={baseY - degArcR - 6}
        fontSize={10}
        textAnchor={flipped ? "start" : "end"}
      >
        60°
      </text>
    )

    // 30° angle at top corner (topX, topY)
    const angle30 = (30 * Math.PI) / 180
    const tArcStartY = topY + degArcR
    const tArcEndX = topX + dir * degArcR * Math.sin(angle30)
    const tArcEndY = topY + degArcR * Math.cos(angle30)
    const tSweep = flipped ? 1 : 0
    ticks.push(
      <path
        key="angle-top"
        d={`M ${topX} ${tArcStartY} A ${degArcR} ${degArcR} 0 0 ${tSweep} ${tArcEndX} ${tArcEndY}`}
        fill="none"
        stroke="#333"
        strokeWidth={1}
      />
    )
    ticks.push(
      <text
        key="angle-top-text"
        x={topX + dir * (degArcR + 6)}
        y={topY + 14}
        fontSize={10}
        textAnchor="start"
      >
        30°
      </text>
    )

    return (
      <HTMLContainer>
        <svg width={w} height={h}>
          <polygon
            points={`${rightX},${rightY} ${baseX},${baseY} ${topX},${topY}`}
            fill="rgba(246,230,180,0.3)"
            stroke="#333"
            strokeWidth={1}
          />

          {ticks}

          {/* Double-headed flip arrow */}
          <g opacity={0.3}>
            <line x1={w / 2 - 12} y1={h / 2} x2={w / 2 + 12} y2={h / 2} stroke="#333" strokeWidth={1.5} />
            <path d={`M ${w / 2 - 12} ${h / 2} l 5 -4 m -5 4 l 5 4`} fill="none" stroke="#333" strokeWidth={1.5} />
            <path d={`M ${w / 2 + 12} ${h / 2} l -5 -4 m 5 4 l -5 4`} fill="none" stroke="#333" strokeWidth={1.5} />
          </g>
        </svg>
      </HTMLContainer>
    )
  }

  override onDoubleClick(shape: SetSquare3060Shape) {
    return {
      id: shape.id,
      type: shape.type as "set-square-30-60",
      props: {
        flipped: !shape.props.flipped,
      },
    }
  }

  override onResize(shape: SetSquare3060Shape, info: TLResizeInfo<SetSquare3060Shape>) {
    const scale = Math.max(Math.abs(info.scaleX), Math.abs(info.scaleY))
    const newW = shape.props.w * scale
    return {
      props: {
        w: newW,
        h: newW * ASPECT,
      },
    }
  }

  indicator(shape: SetSquare3060Shape) {
    return (
      <rect
        width={shape.props.w}
        height={shape.props.h}
      />
    )
  }
}

export const SetSquare3060Icon = () => (
  <svg width={20} height={20} viewBox="0 0 20 20">
    <polygon
      points="2,18 12,18 2,2"
      fill="none"
      stroke="#333"
      strokeWidth={1.5}
    />
    <path d="M 6,18 L 6,14 L 2,14" fill="none" stroke="#333" strokeWidth={1} />
    <text x={5} y={15} fontSize={5} fill="#333">60</text>
    <text x={3} y={8} fontSize={5} fill="#333">30</text>
  </svg>
)

export class SetSquare3060Tool extends BaseBoxShapeTool {
  static override id = "set-square-30-60"
  static override initial = "idle"
  shapeType = "set-square-30-60"
}